import React, { FC, useCallback, useContext, useMemo } from "react";
import {
  Filter,
  RefRowsSchemaItem,
  RowWithEmbeddedMetadata,
  View as IView,
} from "@data-root/types";
import {
  Inline,
  View,
  useAppContext,
  useClientContext,
  useInlineContext,
  useNavigation,
} from "@data-root/react";
import {
  SchemaItemProps,
  UseRowContext,
  UseRowsContext,
  useAsyncCallback,
  useField,
  useRows,
} from "@data-root/react";
import { Typography } from "antd";
import { DeckRows } from "@data-root/plugin-view-deck";
import { v4 } from "uuid";
import { ErrorView } from "@data-root/error-boundary";

const { Title } = Typography;

export const RefRowsDetail: FC<SchemaItemProps<RefRowsSchemaItem>> = ({
  item,
}) => {
  const { core } = useClientContext();
  const { appSpecifier, appId, getTable } = useAppContext();
  const { navigation } = useNavigation();

  const { view } = useInlineContext();
  const { loaded, rowId } = useContext(UseRowContext)!;
  const { displayName } = useField(item);

  const filters = useMemo<Filter[]>(() => {
    if (!loaded) return [];

    return [
      {
        id: item.refField,
        type: "text",
        value: rowId,
      },
    ];
  }, [loaded, item.refField, rowId]);

  const useRefRowsContext = useRows(core, appSpecifier, item.ref, filters);
  const { displayRows, fetch, fetching, totalRows, error } = useRefRowsContext;

  // const [ready, setReady] = useState(false);
  const ready = loaded;

  const refTable = useMemo(() => getTable(item.ref), [getTable, item.ref]);

  const onClick = useAsyncCallback(
    async (refRow: RowWithEmbeddedMetadata) => {
      const appNavigation = navigation.getParent("app" as any)!;
      appNavigation.navigate(item.detailViewId!, {
        rowId: refRow.id,
      });
    },
    [item.detailViewId, navigation]
  );

  const onEdit = useAsyncCallback(
    async (refRow: RowWithEmbeddedMetadata) => {
      const appNavigation = navigation.getParent("app" as any)!;
      appNavigation.navigate(item.formViewId!, {
        rowId: refRow.id,
      });
    },
    [item.formViewId, navigation]
  );

  // useEffect(() => {
  //   if (!ready) initialization();
  // }, [ready, initialization]);

  const primaryHeader = useMemo(() => {
    if (
      view.customizations &&
      view.customizations[item.id] &&
      view.customizations[item.id].primaryHeader
    )
      return view.customizations[item.id].primaryHeader;

    return refTable.label;
  }, [item.id, refTable.label, view.customizations]);

  const secondaryHeader = useMemo(() => {
    if (
      view.customizations &&
      view.customizations[item.id] &&
      view.customizations[item.id].secondaryHeader
    )
      return view.customizations[item.id].secondaryHeader;

    return refTable.secondaryLabel;
  }, [item.id, refTable.secondaryLabel, view.customizations]);

  const image = useMemo(() => {
    if (
      view.customizations &&
      view.customizations[item.id] &&
      view.customizations[item.id].image
    )
      return view.customizations[item.id].image;

    return refTable.imageLabel;
  }, [item.id, refTable.imageLabel, view.customizations]);

  const inlineView = useMemo<IView>(
    () => ({
      id: `inline:${item.ref}`,
      name: `Inline ${item.ref}`,
      description: `Inline ${item.ref}`,
      type: "Deck",
      tableId: item.ref,
      icon: "TableOutlined",
      options: {
        primaryHeader,
        secondaryHeader,
        image,
        actionIds: ["EDIT"],
      },
    }),
    [image, item.ref, primaryHeader, secondaryHeader]
  );

  const getContext = useCallback(
    () => ({
      appId: appSpecifier.appId,
      deploymentId: appSpecifier.deploymentId,
      tableId: item.ref,
      // rowId,
      // row,
    }),
    [appSpecifier.appId, appSpecifier.deploymentId, item.ref]
  );

  const containerId = useMemo(() => v4(), []);

  if (error) return <ErrorView error={error} />;

  return (
    <UseRowsContext.Provider value={useRefRowsContext}>
      <View style={{ width: "100%" }}>
        <Title level={5}>{displayName}</Title>

        {ready && (
          <Inline
            id={containerId}
            table={refTable}
            view={inlineView}
            onClick={onClick}
            onEdit={onEdit}
          >
            <DeckRows
              child
              showGroups={false}
              containerId={containerId}
              getContext={getContext}
              loaded={loaded && ready}
              totalRows={totalRows}
              rows={displayRows}
              fetching={fetching}
              onFetch={fetch}
            />
          </Inline>
        )}
      </View>
    </UseRowsContext.Provider>
  );
};
